<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import MasifiWhatsappQrNumberChecksAPI from 'dashboard/api/masifi/masifiWhatsappQrNumberChecks';
import {
  MASIFI_WA_NUMBER_CHECK_FEATURE,
  isWhatsappQrInbox,
} from 'dashboard/helper/masifi/masifiWhatsappQr';

// "Este número no tiene WhatsApp" antes de iniciar una conversación por WhatsApp QR (spec whatsapp-qr-polish-1 §3.8).
// Montado en ComposeNewConversationForm.vue (upstream; import + 1 tag). Pregunta UNA vez por (bandeja, contacto); el
// backend decide si consulta (solo contactos sin conversación en ese número, con presupuesto anti-baneo) y responde
// `unknown` ante cualquier duda: solo `not_on_whatsapp` muestra el aviso. No bloquea el envío nativo (el backend no lo
// entrega: falla con el mismo motivo).
const props = defineProps({
  targetInbox: { type: Object, default: null },
  selectedContact: { type: Object, default: null },
});
const { t } = useI18n();
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabled = useMapGetter('accounts/isFeatureEnabledonAccount');

const status = ref(null);
// Respuestas ya recibidas por par: volver a un contacto (A → B → A) no vuelve a preguntar (revisión final, minor Task 9).
// `unknown` (sin token, número ocupado, Redis caído) no se recuerda: al volver se pregunta otra vez (re-revisión final).
const answers = new Map();
const pair = computed(() => {
  if (!isWhatsappQrInbox(props.targetInbox) || !props.selectedContact?.id)
    return null;
  if (!isFeatureEnabled.value(accountId.value, MASIFI_WA_NUMBER_CHECK_FEATURE))
    return null;
  return `${props.targetInbox.id}:${props.selectedContact.id}`;
});

watch(
  pair,
  async value => {
    status.value = answers.get(value) || null;
    if (!value || answers.has(value)) return;
    const response = await MasifiWhatsappQrNumberChecksAPI.check(
      props.targetInbox.id,
      props.selectedContact.id
    ).catch(() => null);
    const answer = response?.data?.status;
    if (answer && answer !== 'unknown') answers.set(value, answer);
    if (pair.value === value) status.value = answer || null;
  },
  { immediate: true }
);
</script>

<template>
  <Banner
    v-if="status === 'not_on_whatsapp'"
    color="ruby"
    class="mx-4 my-2"
    data-test="masifi-wa-number-check"
  >
    {{ t('MASIFI_WHATSAPP_QR.NUMBER_CHECK.NOT_ON_WHATSAPP') }}
  </Banner>
</template>
